import { useMemo } from "react";
import {
  type EnrollPhoneNumber,
  PlanSnpType,
  PlanType,
  PlanCategoryType,
  type BasePlan,
  type EnrollFormData,
  GetBenefitsFrom,
  Sex,
  type IndividualRepresentative,
  type SSN,
} from "../@types";
import { isMapdPlanType, isPdPlanType } from "./planTypes";
import { useEnrollPlan } from "./query-hooks/useEnrollPlan";
import { useCurrentPlanYear, useYearPart } from "./yearFlagHelpers";

export const emptyEnrollData: EnrollFormData = {
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  address1: "",
  address2: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  city: "",
  county: undefined,
  dob: { day: "", month: "", year: "" },
  ethnicities: [],
  email: "",
  firstName: "",
  hasSepOtherReason: false,
  individualRepresentative: {
    name: null,
    relationshipCode: null,
    nationalProducerNumber: null,
  } as IndividualRepresentative,
  lastName: "",
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  medicareNumber: "",
  middleInitial: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  primaryCareName: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  races: [],
  sepOtherReason: "",
  sepReasons: [],
  sex: Sex.NONE,
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  state: "",
  zip: "",
};

export const removeExtraWhiteSpace = (str: string): string =>
  str.replaceAll(/\s+/g, " ").trim();

export const formatPhone = (
  phone: EnrollPhoneNumber,
  withDashes = true
): string => {
  const { areaCode, exchangeCode, lineNumber } = phone;
  const delimiter = withDashes ? "-" : "";

  return areaCode && exchangeCode && lineNumber
    ? [phone.areaCode, phone.exchangeCode, phone.lineNumber].join(delimiter)
    : "";
};

export const formatSSN = (ssn: SSN): string =>
  ssn.areaNumber && ssn.groupNumber && ssn.serialNumber
    ? `${ssn.areaNumber}${ssn.groupNumber}${ssn.serialNumber}`
    : "";

export const isCostPlanWithDrugs = (plan: BasePlan): boolean =>
  plan.plan_type !== PlanType.MA &&
  plan.category === PlanCategoryType.COST_1876;

export const isCostPlanWithoutDrugs = (plan: BasePlan): boolean =>
  plan.plan_type === PlanType.MA &&
  plan.category === PlanCategoryType.COST_1876;

export const isPffsMa = (plan: BasePlan): boolean =>
  plan.plan_type === PlanType.MA && plan.category === PlanCategoryType.PFFS;

export const isPffsWithDrugs = (plan: BasePlan): boolean =>
  plan.plan_type !== PlanType.MA && plan.category === PlanCategoryType.PFFS;

export const shouldShowRaceEthnicitySection = (
  plan: BasePlan,
  tmpFeEnableEnrollmentRaceEthnicity: boolean
): boolean =>
  tmpFeEnableEnrollmentRaceEthnicity &&
  plan.category != PlanCategoryType.COST_1876;

export const shouldShowDrugCoverageQuestion = (plan: BasePlan): boolean => {
  const isMAPD = isMapdPlanType(plan.plan_type);
  const isPDP = isPdPlanType(plan.plan_type);
  const isCP_PD = isCostPlanWithDrugs(plan);
  const isPFFS_PD = isPffsWithDrugs(plan);
  const isSNP_DE = plan.snp_type === PlanSnpType.DualEligible;

  return isMAPD || isPDP || isSNP_DE || isPFFS_PD || isCP_PD;
};

export type EnrollmentPage =
  | "Sep"
  | "MedicareInfo"
  | "PersonalInfo"
  | "Address"
  | "GeneralQuestions"
  | "Paying"
  | "Agreement"
  | "TellUsMore"
  | "Review"
  | "Enrolled";

/** The 7 enrollment pages which show which step you are on (not "Sep" or "Enrolled") */
export const stepPages: EnrollmentPage[] = [
  "MedicareInfo",
  "PersonalInfo",
  "Address",
  "GeneralQuestions",
  "Paying",
  "Agreement",
  "Review",
];

export const defaultOrderedPages: EnrollmentPage[] = [...stepPages, "Enrolled"];

export const defaultOrderedPagesWithSep: EnrollmentPage[] = [
  "Sep",
  ...defaultOrderedPages,
];

export const getEnrollmentPages = (
  includeSepPage: boolean
): EnrollmentPage[] =>
  includeSepPage ? defaultOrderedPagesWithSep : defaultOrderedPages;

export function useEnrollmentPages() {
  // * Queries
  const { plan: selectedPlan } = useEnrollPlan();

  // * Flags
  const mctCurrentPlanYear = useCurrentPlanYear();
  const { isOutsideOpenEnrollment } = useYearPart();

  // * Constants
  const selectedPlanIsCurrentPlanYear =
    mctCurrentPlanYear.toString() === selectedPlan?.contract_year;

  const includeSepPage =
    !!selectedPlan &&
    (isOutsideOpenEnrollment || selectedPlanIsCurrentPlanYear) &&
    !isCostPlanWithoutDrugs(selectedPlan);

  const orderedPages = useMemo(
    () => getEnrollmentPages(includeSepPage),
    [includeSepPage]
  );

  return {
    orderedPages,
    includeSepPage,
  };
}
